/**
 * Agent runs, the Manager's plans and the per-tenant subagent roster.
 *
 * Everything the dashboard shows is replayed from `agent_events`, an append-only log the API writes as agents work.
 */

import type { UserRole } from './rbac';
import type { PersonaDefinition } from './personas/definition';

/**
 * Every compiled agent whose runs are logged. `crm` is the Operations Manager's name before 2026-10-18, kept for the runs
 * recorded under it. Personas a tenant hires or an app ships (tenant_personas) have slugs of their own beside these.
 */
export const AGENT_SLUGS = ['manager', 'crm', 'data_analyst', 'analytics', 'research', 'bdr', 'onboarding_operator', 'developer', 'operations_manager', 'bug_fixer', 'professor'] as const;
export type CompiledAgentSlug = (typeof AGENT_SLUGS)[number];

/**
 * A tenant persona's slug (tenant_personas.slug): snake_case, never one of the compiled slugs. Code cannot enumerate
 * them, so the type is a string; `string & {}` keeps the compiled literals suggested where the union is used.
 */
export type PersonaSlug = string & {};

/** The agent a run is recorded as: a compiled agent, or a persona of the tenant. */
export type AgentSlug = CompiledAgentSlug | PersonaSlug;

/** The compiled subagents the Manager can hand plan steps to; a super admin switches each one on per tenant. */
export const SUBAGENT_SLUGS = ['research', 'bdr', 'onboarding_operator', 'developer', 'operations_manager', 'bug_fixer', 'professor'] as const;
export type CompiledSubagentSlug = (typeof SUBAGENT_SLUGS)[number];

/** A subagent the Manager can hand a plan step to: compiled, or an active persona of the tenant. */
export type SubagentSlug = CompiledSubagentSlug | PersonaSlug;

export function isCompiledSubagent(slug: string): slug is CompiledSubagentSlug {
  return (SUBAGENT_SLUGS as readonly string[]).includes(slug);
}

export function isCompiledAgent(slug: string): slug is CompiledAgentSlug {
  return (AGENT_SLUGS as readonly string[]).includes(slug);
}

/** Subagents for Lantern staff (Diggens super admins) alone: they read or fix Lantern itself. */
export const STAFF_ONLY_SUBAGENTS: readonly CompiledSubagentSlug[] = ['onboarding_operator', 'bug_fixer'];

/** The subagents a user with this role is never shown, nor any run they worked in or were planned into. */
export function hiddenAgentsFor(role: UserRole | null | undefined): readonly CompiledSubagentSlug[] {
  return role === 'super_admin' ? [] : STAFF_ONLY_SUBAGENTS;
}

/** Where a roster entry comes from: the code, a hire Chief of Staff proposed, or an app that ships it (`app:<key>`). */
export type AgentSource = 'compiled' | 'hire' | `app:${string}`;

/** A persona slug as a readable name, for runs recorded under a slug the viewer has no roster entry for. */
export function humanizeSlug(slug: string): string {
  return slug
    .split('_')
    .filter((word) => word.length > 0)
    .map((word) => word[0]!.toUpperCase() + word.slice(1))
    .join(' ');
}

/** `job`: an admin gave one specialist this task directly from the team in Settings. */
export type AgentRunTrigger = 'chat' | 'delegation' | 'email' | 'upload' | 'slack' | 'api' | 'job';
/** `waiting`: the run paused because its plan outlived the chat's wait for approval; it carries on once an admin decides. */
export type AgentRunStatus = 'running' | 'waiting' | 'succeeded' | 'failed';
export type PlanDecision = 'approved' | 'rejected' | 'expired';

/** How long the chat waits for an admin to decide a plan that changes data, before its run pauses until they do. */
export const PLAN_LIVE_WAIT_MS = 3 * 60 * 1000;
/** How long a plan that changes data can still be approved; after that it expires with nothing changed. */
export const PLAN_APPROVAL_TIMEOUT_MS = 7 * 24 * 60 * 60 * 1000;
/** A running run with no event for this long is shown as stalled. */
export const RUN_STALLED_AFTER_MS = 2 * 60 * 1000;

export interface PlanStep {
  id: string;
  title: string;
  agent: SubagentSlug;
  /** Exactly what the subagent is asked to do; it is what an approver approves. */
  task: string;
  /** True when the step's agent changes data or spends money. */
  writes: boolean;
  /**
   * True when the step runs its agent's write mode (the Manager set changes_data), which always needs approval. Unset on
   * plans from before it existed, where `writes` alone meant write mode for an agent that has one.
   */
  writeMode?: true;
}

interface AgentEventBase {
  id: string;
  run_id: string;
  root_run_id: string;
  parent_run_id: string | null;
  agent: AgentSlug;
  plan_id: string | null;
  step_id: string | null;
  user_id: string | null;
  created_at: string;
}

export type AgentEvent = AgentEventBase &
  (
    | { kind: 'run_started'; payload: { trigger: AgentRunTrigger; title: string; conversation_id?: string | null; submission_id?: string | null; staff_agents?: SubagentSlug[]; typed?: true } }
    | { kind: 'run_finished'; payload: { status: 'succeeded' | 'failed'; error?: string; turns?: number; tool_calls?: number; output_tokens?: number; output?: string } }
    | { kind: 'run_paused'; payload: { output?: string; turns?: number; tool_calls?: number; output_tokens?: number } }
    | { kind: 'run_resumed'; payload: { decision: 'approved' | 'rejected' } }
    | { kind: 'heartbeat'; payload: Record<string, never> }
    | { kind: 'tool_started'; payload: { tool: string; input: string } }
    | { kind: 'tool_finished'; payload: { tool: string; is_error: boolean; output: string } }
    | { kind: 'plan_proposed'; payload: { goal: string; revision: number; requires_approval: boolean; expires_at: string | null; steps: PlanStep[] } }
    | { kind: 'plan_decided'; payload: { decision: PlanDecision; note?: string; edits?: Record<string, string> } }
    | { kind: 'step_started'; payload: { attempt: number; child_run_id: string } }
    | { kind: 'step_finished'; payload: { attempt: number; status: 'succeeded' | 'failed'; result?: string; error?: string } }
  );

export type AgentEventKind = AgentEvent['kind'];

export interface AgentRosterEntry {
  agent: SubagentSlug;
  displayName: string;
  description: string;
  writes: boolean;
  /** True when its writes need no plan approval: record-level changes a user could make from the screens (the Operations Manager). */
  approvalFree: boolean;
  enabled: boolean;
  updatedAt: string | null;
  source: AgentSource;
  /** The active tenant_personas version for a persona; null for a compiled agent. */
  version: number | null;
  /** The active tenant_personas row for a persona; null for a compiled agent. */
  personaId: string | null;
  /** The persona's definition, for the view in Settings; null for a compiled agent. */
  definition: PersonaDefinition | null;
}

export type TenantPersonaStatus = 'draft' | 'active' | 'disabled';

/** One version of a persona in the tenant, as the API lists it. */
export interface TenantPersonaSummary {
  id: string;
  slug: string;
  version: number;
  status: TenantPersonaStatus;
  source: Exclude<AgentSource, 'compiled'>;
  definition: PersonaDefinition;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AgentRunSummary {
  runId: string;
  agent: AgentSlug;
  trigger: AgentRunTrigger;
  title: string;
  /** The person the run worked for (users.id); null for a run nobody asked for. */
  userId: string | null;
  userName: string | null;
  status: AgentRunStatus;
  /** Running, but no event for RUN_STALLED_AFTER_MS. */
  stalled: boolean;
  startedAt: string;
  lastEventAt: string;
  finishedAt: string | null;
  /** Subagent runs under this one that have not finished. */
  activeChildren: number;
  /** Every agent that worked in this run's tree, the run's own agent included. */
  agents: AgentSlug[];
  plan: { planId: string; goal: string; stepsTotal: number; stepsDone: number; decision: PlanDecision | null; requiresApproval: boolean } | null;
}

/** One agent's work over the last 7 days, delegated runs included, for the team in Settings. */
export interface AgentStats {
  agent: AgentSlug;
  /** Its unfinished runs that are working now, and those paused waiting for an admin's decision. */
  working: number;
  waiting: number;
  runs: number;
  failed: number;
  /** Its newest run, with the top-level run it belongs to, which is the one the activity list opens. */
  lastRun: { runId: string; rootRunId: string; title: string; startedAt: string };
}

export interface AgentRunEvents {
  run: AgentRunSummary;
  events: AgentEvent[];
}

export interface PlanApproval {
  planId: string;
  runId: string;
  goal: string;
  revision: number;
  steps: PlanStep[];
  requestedBy: string | null;
  requestedAt: string;
  expiresAt: string;
  decision: PlanDecision | null;
  decidedBy: string | null;
  decidedAt: string | null;
  note: string | null;
}

/** What the Manager chat stream sends about a plan while the run is still going (SSE frame `{ type: 'plan', event }`). */
export type PlanStreamEvent =
  | { type: 'plan_proposed'; planId: string; goal: string; requiresApproval: boolean; expiresAt: string | null; steps: PlanStep[] }
  | { type: 'plan_decided'; planId: string; decision: PlanDecision; note?: string }
  /** Nobody decided while the chat waited: the plan stays pending and runs once an admin approves it. */
  | { type: 'plan_pending'; planId: string }
  | { type: 'step_status'; planId: string; stepId: string; status: 'running' | 'succeeded' | 'failed' };

export interface PlanApprovals {
  pending: PlanApproval[];
  decided: PlanApproval[];
}
